import { useState, useEffect } from 'react';
import { apiClient } from '../services/api';

export interface Document {
  id: string;
  fileName: string;
  fileSize: number;
  fileUrl: string;
  uploadedAt: Date;
  ocrStatus: string;
  ocrData?: any;
  documentType?: string; // Add document type for matching
}

export interface DocumentTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  isRequired: boolean;
  estimatedTime?: string;
}

export const useDocuments = (caseId?: string) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = async () => {
    if (!caseId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getDocumentsForCase(caseId);
      // Add document type information if not present
      const documentsWithType = data.map((doc: any) => ({
        ...doc,
        documentType: doc.documentType || 'unknown'
      }));
      setDocuments(documentsWithType);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setError(null);
      const data = await apiClient.getDocumentTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch templates');
    }
  };

  const uploadDocument = async (documentType: string, file: File) => {
    if (!caseId) throw new Error('Case ID is required');
    
    try {
      setError(null);
      const newDocument = await apiClient.uploadDocument(caseId, documentType, file);
      // Add document type to the returned document
      const documentWithType = { ...newDocument, documentType };
      setDocuments(prev => [documentWithType, ...prev]);
      return documentWithType;
    } catch (error) {
      console.error('Error uploading document:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload document');
      throw error;
    }
  };

  const deleteDocument = async (documentId: string) => {
    try {
      setError(null);
      await apiClient.deleteDocument(documentId);
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    } catch (error) {
      console.error('Error deleting document:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete document');
      throw error;
    }
  };

  const processOCR = async (documentId: string) => {
    try {
      setError(null);
      const ocrData = await apiClient.processOCR(documentId);
      
      // Update the document in the list with OCR data
      setDocuments(prev => prev.map(doc => 
        doc.id === documentId 
          ? { ...doc, ocrStatus: 'completed', ocrData }
          : doc
      ));
      
      return ocrData;
    } catch (error) {
      console.error('Error processing OCR:', error);
      setError(error instanceof Error ? error.message : 'Failed to process OCR');
      throw error;
    }
  };

  const createTemplate = async (template: Omit<DocumentTemplate, 'id'>) => {
    try {
      setError(null);
      const newTemplate = await apiClient.createDocumentTemplate(template);
      setTemplates(prev => [...prev, newTemplate]);
      return newTemplate;
    } catch (error) {
      console.error('Error creating template:', error);
      setError(error instanceof Error ? error.message : 'Failed to create template');
      throw error;
    }
  };

  const updateTemplate = async (id: string, updates: Partial<DocumentTemplate>) => {
    try {
      setError(null);
      const updatedTemplate = await apiClient.updateDocumentTemplate(id, updates);
      setTemplates(prev => prev.map(template => 
        template.id === id ? updatedTemplate : template
      ));
      return updatedTemplate;
    } catch (error) {
      console.error('Error updating template:', error);
      setError(error instanceof Error ? error.message : 'Failed to update template');
      throw error;
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      setError(null);
      await apiClient.deleteDocumentTemplate(id);
      setTemplates(prev => prev.filter(template => template.id !== id));
    } catch (error) {
      console.error('Error deleting template:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete template');
      throw error;
    }
  };

  useEffect(() => {
    if (caseId) {
      fetchDocuments();
    }
  }, [caseId]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  return {
    documents,
    templates,
    loading,
    error,
    uploadDocument,
    deleteDocument,
    processOCR,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    fetchDocuments,
    fetchTemplates
  };
}; 